import React, { Fragment, useState, useMemo, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import CrewChangeList from './list';
import { CrewChangeDetail } from './detail';
import { ErrorBoundary, SuspenseLoader, useModulePermission, NotFound } from '@bechtel/client-common';
import {
  useApproveCrewChangeRequest,
  useDeclineCrewChangeRequest,
  useCancelCrewChangeRequest,
} from './hooks/use-crew-change';
import { get } from 'lodash';
import { CrewChangeActions } from './types';
import { Scalars } from '@bechtel/types';
import { useCrewChangePermissions } from './hooks/use-crew-change-permission';
import { Alert } from '@bechtel/gds';
import CancelConfirm from './action/cancel-confirm';
import CrewChangeRequestError from './error';

const CrewChange = () => {
  const { canViewCrewChange } = useCrewChangePermissions();
  const { canViewCrewChangeModule } = useModulePermission();

  const [showCancelConfirm, setshowCancelConfirm] = useState(false);
  const [actionCompleted, setActionCompleted] = useState(false);
  const [actionedCrewChangeRequestId, setActionedCrewChangeRequestId] = useState<Scalars['ID']>('');
  const [crewChangeAction, setCrewChangeAction] = useState<CrewChangeActions | null>(null);
  const handleActionCompleted = () => {
    setActionedCrewChangeRequestId('');
    setActionCompleted(true);
  };

  const [
    approveCrewChangeRequest,
    { loading: approveProgress, error: approveError, data: approvedData },
  ] = useApproveCrewChangeRequest(handleActionCompleted);
  const approveCrewChangeRequestError = get(approvedData, 'approveCrewChangeRequest.error') || approveError;

  const [
    declineCrewChangeRequest,
    { loading: declineProgress, error: declineError, data: declinedData },
  ] = useDeclineCrewChangeRequest(handleActionCompleted);
  const declineCrewChangeRequestError = get(declinedData, 'declineCrewChangeRequest.error') || declineError;

  const [
    cancelCrewChangeRequest,
    { loading: cancelProgress, error: cancelError, data: cancelledData },
  ] = useCancelCrewChangeRequest(handleActionCompleted);
  const cancelCrewChangeRequestError = get(cancelledData, 'cancelCrewChangeRequest.error') || cancelError;

  const crewChangeRequestError = useMemo(() => {
    if (!crewChangeAction) return null;
    if (crewChangeAction == CrewChangeActions.APPROVE && approveCrewChangeRequestError)
      return approveCrewChangeRequestError;
    if (crewChangeAction == CrewChangeActions.DECLINE && declineCrewChangeRequestError)
      return declineCrewChangeRequestError;
    if (crewChangeAction == CrewChangeActions.CANCEL && cancelCrewChangeRequestError)
      return cancelCrewChangeRequestError;
    return null;
  }, [approveCrewChangeRequestError, declineCrewChangeRequestError, cancelCrewChangeRequestError, crewChangeAction]);

  if (!canViewCrewChangeModule) {
    return <Alert>Sorry, Crew Change module is not activated for this project.</Alert>;
  }

  if (!canViewCrewChange) {
    return <Alert>Sorry, you do not have rights to view crew change</Alert>;
  }

  const handleAction = (crewChangeRequestId: string, action: CrewChangeActions) => {
    setActionedCrewChangeRequestId(crewChangeRequestId);
    setCrewChangeAction(action);
    if (action == CrewChangeActions.APPROVE) {
      approveCrewChangeRequest({
        variables: {
          crewChangeRequestId: crewChangeRequestId,
        },
      });
    } else if (action == CrewChangeActions.DECLINE) {
      declineCrewChangeRequest({
        variables: {
          crewChangeRequestId: crewChangeRequestId,
        },
      });
    } else if (action == CrewChangeActions.CANCEL) setshowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    setshowCancelConfirm(false);
    cancelCrewChangeRequest({
      variables: {
        crewChangeRequestId: actionedCrewChangeRequestId,
      },
    });
  };

  return (
    <Fragment>
      <ErrorBoundary>
        <Routes>
          <Route
            path={`/:id`}
            element={
              <CrewChangeDetail
                onAction={handleAction}
                actionedCrewChangeRequestId={actionedCrewChangeRequestId}
                approveProgress={approveProgress}
                declineProgress={declineProgress}
                cancelProgress={cancelProgress}
              />
            }
          />
          <Route
            path="/"
            element={
              <CrewChangeDetail
                onAction={handleAction}
                actionedCrewChangeRequestId={actionedCrewChangeRequestId}
                approveProgress={approveProgress}
                declineProgress={declineProgress}
                cancelProgress={cancelProgress}
              />
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
      <ErrorBoundary>
        <CrewChangeList
          onAction={handleAction}
          actionedCrewChangeRequestId={actionedCrewChangeRequestId}
          approveProgress={approveProgress}
          declineProgress={declineProgress}
          cancelProgress={cancelProgress}
          actionCompleted={actionCompleted}
          setActionCompleted={setActionCompleted}
        />
      </ErrorBoundary>
      {crewChangeRequestError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CrewChangeRequestError
              crewChangeRequestError={crewChangeRequestError}
              crewChangeRequestId={actionedCrewChangeRequestId}
              crewChangeAction={crewChangeAction}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {showCancelConfirm && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <CancelConfirm
              onCancel={() => {
                setshowCancelConfirm(false);
                setActionedCrewChangeRequestId('');
              }}
              onConfirm={handleCancelConfirm}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};

export default CrewChange;
